import { userProfile$ } from '@psl/functions/firebase/observables/userProfile$';
import { WithID } from '@psl/functions/firebase/rxfireExtensions';
import { Profile } from '@psl/models/ClientModels';
import { StackActions, StackNavigationState } from '@react-navigation/native';
import { useCallback, useEffect, useRef } from 'react';
import { StyleProp, TextStyle } from 'react-native';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { useIsMobileLayout } from '../../../components/styles/responsive';
import { currentUserId } from '../../../functions/auth/utils';
import { isDebugEnabled } from '../../../functions/debugging';
import useBehaviorSubject from '../../../functions/hooks/useBehaviorSubject';
import { CancelError } from '../../../functions/hooks/useOnPromise';
import { usePersistFn } from '../../../functions/hooks/usePersistFn';
import { useSafeTopNavigation } from '../../../functions/navigation/useSafeTopNavigation';
import { AppModalNavigation } from '../../../functions/navigation/utils/routeHelpers';
import { makeDebug } from '../../../utils/makeDebug';
import { AppModalParams } from '../../Routes';

export type LoginPresentationType = 'modal' | 'new-onboarding-flow' | 'auto';

const debug = makeDebug('login-presenter', isDebugEnabled('loginPresenter'));

export const loginPresentationSuppressed$ = new BehaviorSubject<boolean>(false);

export const useIsLoginPresentationSuppressed = () =>
  useBehaviorSubject(loginPresentationSuppressed$)[0];

export const useSuppressLoginPresentation = (at: string) => {
  useEffect(() => {
    debug('presentation temporarily suppressed at %s', at);
    loginPresentationSuppressed$.next(true);
    return () => {
      loginPresentationSuppressed$.next(false);
      debug('presentation restored from %s', at);
    };
  }, [at]);
};

export interface PresentLoginOptions {
  skipOnboarding?: boolean;
  avatar?: string;
  title?: string;
  headerText?: string;
  bodyText?: string;
  headerTextStyle?: StyleProp<TextStyle>;
  type?: LoginPresentationType;
  proactively?: boolean;
  withProfile?: boolean;
}

// only allow one instance of login screen
type PresentationType = 'screen' | 'modal';
let presented: PresentationType | undefined = undefined;

const kErrorMessages = {
  alreadyPresented:
    'presentation rejected because login screen already presented',
  presentationSuppressed:
    'presentation rejected because login presentation is suppressed',
};

export const usePresentLogin = (defaultOptions: PresentLoginOptions = {}) => {
  const cancelled = useRef<(e: Error) => void>();
  const [isLoginPresentationSuppressed] = useBehaviorSubject(
    loginPresentationSuppressed$,
  );
  const isMobile = useIsMobileLayout();
  const withTopNavigation = useSafeTopNavigation();

  return usePersistFn(
    async (
      options: PresentLoginOptions = {},
    ): Promise<WithID<Profile> | undefined> => {
      return new Promise<WithID<Profile> | undefined>((resolve, reject) => {
        const type = options.type ?? defaultOptions.type ?? 'auto';
        const proactively =
          options.proactively ?? defaultOptions.proactively ?? true;

        if (presented) {
          debug(kErrorMessages.alreadyPresented);
          reject(new CancelError(kErrorMessages.alreadyPresented));
          return;
        }

        if (isLoginPresentationSuppressed && proactively === false) {
          debug(kErrorMessages.presentationSuppressed);
          reject(new CancelError(kErrorMessages.presentationSuppressed));
          return;
        }

        cancelled.current = reject;

        const begin = (type: PresentationType) => {
          debug('login %s presented', type);
          presented = type;
        };

        const dismiss = () => {
          debug('dismiss login %s', presented);

          if (cancelled.current == null) {
            presented = undefined;
            debug('already dismissed, skipped');
            return;
          }

          cancelled.current(new CancelError('login ended'));
          cancelled.current = undefined;

          debug('login %s dismissed', presented);
          presented = undefined;
        };

        const finish = async (
          nav: AppModalNavigation,
          state: StackNavigationState<AppModalParams>,
        ) => {
          cancelled.current = undefined;
          debug('login %s finished', presented);

          if (presented === 'screen') {
            try {
              nav.reset(state);
              debug('navigation state is reset');
            } catch (err) {
              console.error(
                'An error occurred while resetting pre-login navigation state: ',
                err,
              );
              nav.popToTop();
            }
          }

          presented = undefined;

          const profile = options?.withProfile
            ? await firstValueFrom(userProfile$(currentUserId()!))
            : undefined;

          resolve(profile);
        };

        if (type === 'new-onboarding-flow' || (isMobile && type === 'auto')) {
          withTopNavigation(nav => {
            const state = nav.getState();
            nav.dispatch(
              StackActions.push('onboarding', {
                initialRouteName: '_phoneNumber',
                onCreated: begin.bind(null, 'screen'),
                onCancel: dismiss,
                onDone: finish.bind(null, nav, state),
              }),
            );
          });
          return;
        }

        withTopNavigation(nav => {
          const state = nav.getState();
          nav.dispatch(
            StackActions.push('_auth', {
              screen: 'login',
              onCreated: begin.bind(null, 'modal'),
              onClosed: dismiss,
              params: {
                skipOnboarding:
                  options.skipOnboarding ?? defaultOptions.skipOnboarding,
                avatar: options.avatar ?? defaultOptions.avatar,
                headerText: options.headerText ?? defaultOptions.headerText,
                title: options.title ?? defaultOptions.title,
                bodyText: options.bodyText ?? defaultOptions.bodyText,
                headerTextStyle:
                  options.headerTextStyle ?? defaultOptions.headerTextStyle,
                onFinish: finish.bind(null, nav, state),
              },
            }),
          );
        });
      });
    },
  );
};

export const useEnsureLogin = () => {
  const presentLogin = usePresentLogin();
  return useCallback(
    async (
      handler: (profile: WithID<Profile> | undefined) => void,
      options?: PresentLoginOptions,
    ) => {
      let profile: WithID<Profile> | undefined;
      if (currentUserId() != null) {
        if (options?.withProfile) {
          profile = await firstValueFrom(userProfile$(currentUserId()!));
        }
      } else {
        try {
          profile = await presentLogin(options);
        } catch (e) {
          if (e instanceof CancelError) {
            return;
          }
          console.error(e);
          return;
        }
      }
      handler(profile);
    },
    [presentLogin],
  );
};

export const useWithAuthenticated = (options?: PresentLoginOptions) => {
  const ensureLogin = useEnsureLogin();
  return usePersistFn(
    async (handler: (profile: WithID<Profile> | undefined) => void) => {
      await ensureLogin(handler, options);
    },
  );
};
